import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { lastValueFrom, take } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { AuthService } from '../../../auth/services/auth.service';
import { SubdomainService } from '../../../../core/services/subdomain.service';

export interface PatrolSetup {
  botInstalled: boolean;
  botReady: boolean;
  guildCount: number;
  loggedInAs: string | null;
  inviteUrl: string | null;
  startupError: string | null;
}

export type PatrolFleetGuildStatus = 'active' | 'pending_link' | 'suspended';

export interface PatrolFleetGuild {
  registryId: string;
  guildId: string | null;
  guildName: string;
  status: PatrolFleetGuildStatus;
  ownerUserId: string;
  ownerUserName: string;
  memberCount: number;
  linkedMemberCount: number;
  botPresent: boolean;
  allowlisted: boolean;
  linkedAt: string | null;
  createdAt: string;
}

export interface PatrolFleetUnregisteredGuild {
  guildId: string;
  guildName: string;
  memberCount: number;
  allowlisted: boolean;
}

export interface PatrolFleet {
  botReady: boolean;
  botInstalled: boolean;
  guildCount: number;
  loggedInAs: string | null;
  inviteUrl: string | null;
  startupError: string | null;
  allowlistEnforced: boolean;
  guilds: PatrolFleetGuild[];
  unregisteredBotGuilds: PatrolFleetUnregisteredGuild[];
}

export type PatrolRobloxBridgeStatus = 'never_seen' | 'online' | 'stale';

export interface PatrolRobloxFleetExperience {
  registryId: string;
  universeId: string | null;
  experienceName: string;
  status: PatrolFleetGuildStatus;
  ownerUserId: string;
  ownerUserName: string;
  linkedMemberCount: number;
  bridgeStatus: PatrolRobloxBridgeStatus | null;
  bridgeLastSeenAt: string | null;
  allowlisted: boolean;
  ingestAllowed: boolean;
  linkedAt: string | null;
  createdAt: string;
}

export interface PatrolRobloxFleet {
  ingestEnabled: boolean;
  allowlistEnforced: boolean;
  allowedUniverseIds: string[];
  experienceCount: number;
  experiences: PatrolRobloxFleetExperience[];
}

const PATROL_SETUP_QUERY_KEY = ['admin', 'patrol', 'setup'] as const;
const PATROL_FLEET_QUERY_KEY = ['admin', 'patrol', 'fleet'] as const;
const PATROL_ROBLOX_FLEET_QUERY_KEY = ['admin', 'patrol', 'roblox-fleet'] as const;

@Injectable({ providedIn: 'root' })
export class PatrolService {
  #http = inject(HttpClient);
  #authService = inject(AuthService);
  #subdomain = inject(SubdomainService);

  setupQuery = injectQuery(() => ({
    queryKey: PATROL_SETUP_QUERY_KEY,
    retry: 1,
    queryFn: () =>
      lastValueFrom(
        this.#http
          .get<PatrolSetup>(`${environment.baseUrl}/bots/discord/patrol/setup`)
          .pipe(take(1)),
      ),
    enabled:
      this.#authService.isAuthenticated() &&
      this.#subdomain.isAdminSubdomain(),
  }));

  fleetQuery = injectQuery(() => ({
    queryKey: PATROL_FLEET_QUERY_KEY,
    retry: 1,
    queryFn: () =>
      lastValueFrom(
        this.#http
          .get<PatrolFleet>(`${environment.baseUrl}/bots/discord/patrol/fleet`)
          .pipe(take(1)),
      ),
    enabled:
      this.#authService.isAuthenticated() &&
      this.#subdomain.isAdminSubdomain(),
  }));

  robloxFleetQuery = injectQuery(() => ({
    queryKey: PATROL_ROBLOX_FLEET_QUERY_KEY,
    retry: 1,
    queryFn: () =>
      lastValueFrom(
        this.#http
          .get<PatrolRobloxFleet>(
            `${environment.baseUrl}/bots/patrol/roblox-fleet`,
          )
          .pipe(take(1)),
      ),
    enabled:
      this.#authService.isAuthenticated() &&
      this.#subdomain.isAdminSubdomain(),
  }));

  refetchSetup(): void {
    this.setupQuery.refetch();
  }

  refetchFleet(): void {
    this.fleetQuery.refetch();
  }

  refetchRobloxFleet(): void {
    this.robloxFleetQuery.refetch();
  }

  refreshAll(): void {
    this.refetchSetup();
    this.refetchFleet();
    this.refetchRobloxFleet();
  }

  async addToAllowlist(guildId: string): Promise<void> {
    await lastValueFrom(
      this.#http
        .post(`${environment.baseUrl}/bots/discord/patrol/allowlist`, {
          guildId,
        })
        .pipe(take(1)),
    );
    this.refetchFleet();
  }

  async removeFromAllowlist(guildId: string): Promise<void> {
    await lastValueFrom(
      this.#http
        .delete(
          `${environment.baseUrl}/bots/discord/patrol/allowlist/${encodeURIComponent(guildId)}`,
        )
        .pipe(take(1)),
    );
    this.refetchFleet();
  }

  async addUniverseToAllowlist(universeId: string): Promise<void> {
    await lastValueFrom(
      this.#http
        .post(`${environment.baseUrl}/bots/patrol/roblox/allowlist`, {
          universeId,
        })
        .pipe(take(1)),
    );
    this.refetchRobloxFleet();
  }

  async removeUniverseFromAllowlist(universeId: string): Promise<void> {
    await lastValueFrom(
      this.#http
        .delete(
          `${environment.baseUrl}/bots/patrol/roblox/allowlist/${encodeURIComponent(universeId)}`,
        )
        .pipe(take(1)),
    );
    this.refetchRobloxFleet();
  }
}
